/**
 * Display-layer normalisation for a trainer's `userLocation`.
 *
 * The stored field is free text and inconsistent: roughly 40% of the live roster
 * holds a raw postal code ("550227"), a postal-suffixed string
 * ("Singapore 692122") or a full street address
 * ("428 ANG MO KIO AVENUE 3 CHONG BOON HEIGHTS SINGAPORE 560428") instead of a
 * locality. Trainer cards lead with locality, so rendering the raw value puts
 * postcodes on the page.
 *
 * This normalises for DISPLAY only - it does not write back to the provider
 * record. The underlying data still needs fixing at source; until then every
 * surface that shows a locality should go through here so they agree.
 *
 * Resolution order:
 *   1. a known Singapore area name appearing in the string (most reliable)
 *   2. the postal sector (first two digits of a 6-digit code) -> district area
 *   3. "Singapore"
 *
 * The postal-sector table is the standard Singapore sector-to-district mapping.
 * It was validated against roster records that carry BOTH a code and a place
 * name: 464219 -> Bedok, 560428 -> Ang Mo Kio, 307987 -> Newton/Novena.
 */

import { countryList } from './constants'

const SG_LOCALITIES = countryList.find((c) => c.country_cd === "sg")?.localityList ?? []

// Localities that have a /sg/personal-trainer/trainer-list/{locality} page.
// Derived from the same list that page's getStaticPaths builds from, so the two
// cannot drift - this used to be a hand-copied array and drifting is exactly how
// profiles ended up linking at pages that do not exist.
// "Singapore" is excluded: the island-wide page exists, but it is the hub's job.
export const LINKABLE_LOCALITIES = SG_LOCALITIES.filter((l) => l !== "Singapore")

// Areas we can recognise in free text but that have no locality page of their own.
// Listed longest-first at match time so "Bukit Batok" wins over "Bukit".
const EXTRA_AREAS = [
    "Choa Chu Kang", "Punggol", "Novena", "Bukit Timah", "Queenstown", "Tiong Bahru",
    "Bukit Merah", "Telok Blangah", "Harbourfront", "Pasir Panjang", "Changi",
    "Loyang", "Sembawang", "Kranji", "Tengah", "Lim Chu Kang", "Boon Lay", "Tuas",
    "Jurong West", "Jurong", "Seletar", "Potong Pasir", "Macpherson", "Balestier",
    "Katong", "Joo Chiat", "Siglap", "Newton", "River Valley", "Holland Village",
    "Farrer Park", "Upper Thomson", "Springleaf", "Braddell", "Dairy Farm", "Hillview",
    // Estates whose names contain a locality's name but lie outside it; matched
    // longest-first, so "Tanglin Halt" wins over "Tanglin". Same list as the
    // backend's SingaporeLocality.
    "Tanglin Halt", "Geylang Bahru"
]

const KNOWN_AREAS = [...LINKABLE_LOCALITIES, ...EXTRA_AREAS]
    .filter((a) => a !== "Singapore")
    .sort((a, b) => b.length - a.length)

// Postal sector (first two digits) -> the area we show for it.
const POSTAL_SECTOR_AREA = {
    "01": "Raffles Place", "02": "Raffles Place", "03": "Raffles Place",
    "04": "Raffles Place", "05": "Raffles Place", "06": "Raffles Place",
    "07": "Tanjong Pagar", "08": "Tanjong Pagar",
    "09": "Telok Blangah", "10": "Telok Blangah",
    "11": "Pasir Panjang", "12": "Pasir Panjang", "13": "Clementi",
    "14": "Bukit Merah", "15": "Queenstown", "16": "Bukit Merah",
    "17": "Raffles Place",
    "18": "Rochor", "19": "Rochor",
    "20": "Little India", "21": "Little India",
    "22": "Orchard", "23": "Orchard",
    "24": "Tanglin", "25": "Tanglin", "26": "Bukit Timah", "27": "Tanglin",
    "28": "Novena", "29": "Novena", "30": "Novena",
    "31": "Balestier", "32": "Toa Payoh", "33": "Serangoon",
    "34": "Macpherson", "35": "Macpherson", "36": "Potong Pasir", "37": "Potong Pasir",
    "38": "Geylang", "39": "Geylang", "40": "Eunos", "41": "Paya Lebar",
    "42": "Katong", "43": "Joo Chiat", "44": "Marine Parade", "45": "Marine Parade",
    "46": "Bedok", "47": "Bedok", "48": "Siglap",
    "49": "Loyang", "50": "Changi", "81": "Changi",
    "51": "Tampines", "52": "Pasir Ris",
    "53": "Serangoon", "54": "Hougang", "55": "Hougang", "82": "Punggol",
    "56": "Bishan", "57": "Ang Mo Kio",
    "58": "Bukit Timah", "59": "Clementi",
    "60": "Jurong East", "61": "Jurong East", "62": "Boon Lay",
    "63": "Boon Lay", "64": "Jurong East",
    "65": "Bukit Batok", "66": "Bukit Batok", "67": "Bukit Panjang", "68": "Bukit Panjang",
    "69": "Choa Chu Kang", "70": "Tengah", "71": "Lim Chu Kang",
    "72": "Woodlands", "73": "Woodlands",
    "75": "Yishun", "76": "Sembawang",
    "77": "Upper Thomson", "78": "Springleaf",
    "79": "Seletar", "80": "Seletar"
}

/**
 * Best-effort locality label for a raw userLocation string.
 * Always returns something renderable; falls back to "Singapore".
 */
export const normalizeLocality = (raw) => {
    const value = (raw || "").trim()
    if (!value) return "Singapore"

    const haystack = value.toLowerCase()
    const named = KNOWN_AREAS.find((area) => haystack.includes(area.toLowerCase()))
    if (named) return named

    // Any standalone 6-digit run is treated as a postal code.
    const postal = value.match(/(?:^|\D)(\d{6})(?:\D|$)/)
    if (postal) {
        const area = POSTAL_SECTOR_AREA[postal[1].slice(0, 2)]
        if (area) return area
    }

    return "Singapore"
}

/**
 * True when the locality has its own trainer-list page worth linking to.
 */
export const isLinkableLocality = (locality) => LINKABLE_LOCALITIES.includes(locality)

/**
 * Nearest EXTRA_AREAS -> LINKABLE_LOCALITIES fallback.
 *
 * normalizeLocality recognises more areas than the site has pages for, and the
 * trainer-list route is `fallback: false` - an unknown locality is a hard 404,
 * not an empty page. These links sit in server-rendered HTML and get crawled,
 * so every recognised area needs a destination that exists. Sending a Novena
 * trainer to Toa Payoh keeps the link geographically useful; dropping it would
 * put the profile back to dead-ending.
 *
 * Every EXTRA_AREAS entry must appear here, and every value must be linkable.
 */
const NEAREST_LINKABLE = {
    "Choa Chu Kang": "Bukit Panjang", "Punggol": "Sengkang", "Novena": "Toa Payoh",
    "Bukit Timah": "Tanglin", "Queenstown": "Clementi", "Tiong Bahru": "Tanjong Pagar",
    "Bukit Merah": "Tanjong Pagar", "Telok Blangah": "Tanjong Pagar",
    "Harbourfront": "Sentosa", "Pasir Panjang": "Clementi", "Changi": "Tampines",
    "Loyang": "Pasir Ris", "Sembawang": "Yishun", "Kranji": "Woodlands",
    "Tengah": "Bukit Batok", "Lim Chu Kang": "Bukit Panjang", "Boon Lay": "Jurong East",
    "Tuas": "Jurong East", "Jurong West": "Jurong East", "Jurong": "Jurong East",
    "Seletar": "Sengkang", "Potong Pasir": "Toa Payoh", "Macpherson": "Paya Lebar",
    "Balestier": "Toa Payoh", "Katong": "Marine Parade", "Joo Chiat": "Marine Parade",
    "Siglap": "Bedok", "Newton": "Orchard", "River Valley": "Orchard",
    "Holland Village": "Tanglin", "Farrer Park": "Little India",
    "Upper Thomson": "Bishan", "Springleaf": "Yishun", "Braddell": "Toa Payoh",
    "Dairy Farm": "Bukit Panjang", "Hillview": "Bukit Panjang",
    "Tanglin Halt": "Clementi", "Geylang Bahru": "Geylang"
}

/**
 * The trainer-list page to send a visitor to for a normalizeLocality output.
 * Always returns a locality that has a page; "Singapore" (the island-wide list)
 * when there is nothing closer.
 */
export const getLinkableLocality = (locality) =>
    isLinkableLocality(locality) ? locality : (NEAREST_LINKABLE[locality] ?? "Singapore")
